import { useState } from 'react';
import { AlertTriangle, UserRoundCog } from 'lucide-react';
import type { SchoolRequestReportDto } from '../api/school-requests.api';
import { formatUnixTimestamp } from '../../../shared/utils';

// @ts-ignore - JSX component
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';

interface ApprovalDialogProps {
    isOpen: boolean;
    onClose: () => void;
    request: SchoolRequestReportDto | null;
    onApprove: (id: string) => void;
    onReject: (reason: string) => void;
    loading: boolean;
}

export function ApprovalDialog({
    isOpen,
    onClose,
    request,
    onApprove,
    onReject,
    loading
}: ApprovalDialogProps) {
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectInput, setShowRejectInput] = useState(false);
    const [error, setError] = useState('');

    /* Parintele nu demonteaza dialogul pe calea de succes (`selectedRequest`
       ramane setat intre doua cereri consecutive) — vezi `handleClose` mai jos,
       care rezolva cazul cand utilizatorul chiar inchide dialogul. Dar
       `onSuccess` din pagina inchide DOAR prin `setApprovalDialogOpen(false)`,
       fara sa treaca prin `onOpenChange`, deci `handleClose` nu ruleaza pe acea
       cale, si `rejectReason` din randul respins ramanea in casuta la
       urmatoarea cerere (P0-2). Fix: la fel ca `PhoneNotesDialog`, starea se
       reseteaza la TRANZITIA lui `isOpen`, in randare, nu intr-un `useEffect`
       — un `useEffect` cu `[isOpen]` ar reseta si cat timp dialogul e deschis,
       de fiecare data cand `request` se schimba din alt motiv. */
    /* Bifa de preluare urmeaza acelasi tipar, cu o conditie in plus: se reseteaza si cand se
       schimba RANDUL, nu doar la tranzitia lui `isOpen`. O bifa pusa pentru un coordonator
       n-are voie sa ajunga, nici teoretic, pe cererea altei scoli. */
    const [confirmaPreluare, setConfirmaPreluare] = useState(false);
    const [eraDeschis, setEraDeschis] = useState(isOpen);
    const [randAnterior, setRandAnterior] = useState(request?.id);
    if (isOpen !== eraDeschis || request?.id !== randAnterior) {
        setEraDeschis(isOpen);
        setRandAnterior(request?.id);
        setRejectReason('');
        setShowRejectInput(false);
        setError('');
        setConfirmaPreluare(false);
    }

    const handleApprove = () => {
        if (!request?.id) return;
        setError('');
        onApprove(request.id);
    };

    const handleReject = () => {
        if (!request?.id) return;
        if (!rejectReason.trim()) {
            setError('Motivul respingerii este obligatoriu');
            return;
        }
        setError('');
        onReject(rejectReason);
    };

    const handleClose = () => {
        setRejectReason('');
        setShowRejectInput(false);
        setError('');
        setConfirmaPreluare(false);
        onClose();
    };

    if (!request) return null;

    const estePreluare = request.cererePreluare === true;
    const coordonatorActual = request.profesorActualNume || '-';
    const ultimaActivitate = request.profesorActualUltimaActivitate
        ? formatUnixTimestamp(request.profesorActualUltimaActivitate)
        : null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Aprobarea Cererii de Înscriere</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* School Info */}
                    <div>
                        <label className="text-small font-medium text-gray-700">Școală:</label>
                        <p className="text-small text-gray-900">{request.numeInstitutieInvatamant || '-'}</p>
                    </div>

                    <div>
                        <label className="text-small font-medium text-gray-700">Cod SIIIR:</label>
                        <p className="text-small text-gray-900">{request.codSiiirUnitate || '-'}</p>
                    </div>

                    <div>
                        <label className="text-small font-medium text-gray-700">Profesor:</label>
                        <p className="text-small text-gray-900">{request.professor?.nume || '-'}</p>
                        {/* Era `text-xs` (12px). Emailul e datul dupa care se recunoaste
                            profesorul cand doua nume seamana — nu e text decorativ. */}
                        <p className="text-small break-all text-gray-700">{request.professor?.email || '-'}</p>
                    </div>

                    {/* Preluare: aprobarea scoate scoala de la coordonatorul actual. Cutie proprie,
                        separata de caseta «ireversibil», cu aceiasi tokeni (vezi contrastul masurat
                        mai jos), dar cu pictograma de schimbare de persoana, nu AlertTriangle: cele
                        doua casete spun lucruri diferite si nu trebuie citite ca una singura.
                        Datele vin din randul grilei; niciun apel nou. */}
                    {estePreluare && (
                        <div className="flex items-start gap-2 rounded-md border border-warning bg-warning/5 p-3">
                            <UserRoundCog className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
                            <p className="text-small text-warning">
                                Preluare: școala are coordonator actual {coordonatorActual},{' '}
                                {ultimaActivitate
                                    ? `ultima activitate ${ultimaActivitate}`
                                    : 'ultima activitate: necunoscută'}
                            </p>
                        </div>
                    )}

                    {/* Avertisment. Fundalul e `/5`, nu `/10`: `text-warning` (#B45309)
                        pe `bg-warning/10` cade sub 4,5:1. Iconita lucide inlocuieste
                        emoji-ul ⚠️, care se randeaza diferit pe fiecare platforma si
                        pe Android e adesea invizibil pe fundal deschis. */}
                    {/* Chenar PLIN, nu `/20`. Regula, ca sa nu se redecida de fiecare data:
                        o caseta care anunta CONSECINTA unei actiuni primeste chenar plin
                        `border-warning`; una care doar adauga context ramane rand cu
                        pictograma si text. Asta e primul fel — e ultimul lucru citit
                        inainte de apasare. (Contra-exemplu deliberat lasat asa:
                        `ImageUploadReport` „Lipseste descrierea", care raporteaza o stare
                        a datelor, nu o consecinta.)

                        Masurat pe build, chenarul are DOUA fete si amandoua conteaza:
                          spre interior  4,68  fata de umplutura #fbf6f2
                          spre exterior  5,02  fata de suprafata dialogului
                        Cu `/20` erau 1,41 — practic invizibil.

                        ⚠️ Umplutura `/5` ramane NEATINSA, desi e la 1,07 fata de suprafata:
                        cei 4,68 ai textului sunt masurati PE ea. Daca dispare, textul se
                        muta pe alb si cifra nu mai descrie ecranul. */}
                    <div className="flex items-start gap-2 rounded-md border border-warning bg-warning/5 p-3">
                        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
                        <p className="text-small text-warning">
                            Această acțiune este ireversibilă
                        </p>
                    </div>

                    {/* Confirmarea preluarii. `<label>` infasoara bifa si textul, deci tot randul e
                        tinta de atingere; `min-h-11` sub `md` il duce la 44px, iar pe desktop ramane
                        la inaltimea textului. Doar cand cererea e preluare: altfel dialogul ramane
                        exact cum era. */}
                    {estePreluare && !showRejectInput && (
                        <label
                            htmlFor="confirma-preluare"
                            className="flex min-h-11 cursor-pointer items-center gap-2 md:min-h-0"
                        >
                            <Checkbox
                                id="confirma-preluare"
                                checked={confirmaPreluare}
                                onCheckedChange={(v) => setConfirmaPreluare(v === true)}
                                disabled={loading}
                            />
                            <span className="text-small text-gray-900">
                                Confirm că înlocuiesc coordonatorul actual, {coordonatorActual}.
                            </span>
                        </label>
                    )}

                    {/* Reject Reason Input */}
                    {showRejectInput && (
                        <div>
                            <label className="text-small font-medium text-gray-700">
                                Motiv Respingere <span className="text-error">*</span>
                            </label>
                            {/* `text-base md:text-sm`, ca in `Input`-ul shadcn: sub 16px,
                                iOS mareste pagina la focus si utilizatorul ramane cu ea marita. */}
                            <textarea
                                className="mt-1 w-full rounded-md border border-input px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-magenta md:text-sm"
                                rows={4}
                                value={rejectReason}
                                onChange={(e) => {
                                    setRejectReason(e.target.value);
                                    setError('');
                                }}
                                placeholder="Introduceți motivul respingerii..."
                                disabled={loading}
                            />
                            {error && <p className="mt-1 text-small text-error">{error}</p>}
                        </div>
                    )}

                    {/* Bara de actiuni. Sub `sm` butoanele stau pe coloana, in ordine
                        inversa: actiunea principala ramane cea mai apropiata de deget,
                        jos. Inaltimea de 44px sub `md` vine din `Button` (cva), nu de aici. */}
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        {!showRejectInput ? (
                            <>
                                <Button variant="outline" onClick={handleClose} disabled={loading}>
                                    Anulare
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => setShowRejectInput(true)}
                                    disabled={loading}
                                >
                                    Respinge
                                </Button>
                                <Button
                                    onClick={handleApprove}
                                    disabled={loading || (estePreluare && !confirmaPreluare)}
                                >
                                    Aprobă
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowRejectInput(false);
                                        setRejectReason('');
                                        setError('');
                                    }}
                                    disabled={loading}
                                >
                                    Înapoi
                                </Button>
                                <Button variant="destructive" onClick={handleReject} disabled={loading}>
                                    Confirmă Respingere
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
